import type { InstrumentRunFormValues } from '@/types';

type FormSample = InstrumentRunFormValues['samples'][number];

/** Total number of offending rows (e.g. two rows sharing a key → 2), not the number of collisions. */
function countDuplicatesBy(
  samples: InstrumentRunFormValues['samples'],
  keyOf: (sample: FormSample) => string | undefined,
): number {
  const counts = new Map<string, number>();
  for (const s of samples) {
    const key = keyOf(s);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  let total = 0;
  for (const n of counts.values()) {
    if (n > 1) total += n;
  }
  return total;
}

/** Mirrors the backend's `uq_app_sample_well_demux_run_active` index — (well_position, demux_index)
 *  per run, active rows only. Adding flow cell or lane would make this looser than the DB, passing
 *  rows that then fail at submit. The `|` keeps well "A11"/demux 1 distinct from well "A1"/demux 11. */
export function plateCoordinateKey(s: FormSample): string | undefined {
  const well = s.well_position?.trim();
  return well ? `${well}|${s.demux_index}` : undefined;
}

/** Counted separately from backend "already exists" conflicts so the two get distinct copy. */
export function countDuplicateSampleNames(samples: InstrumentRunFormValues['samples']): number {
  return countDuplicatesBy(samples, s => s.name?.trim());
}

/** Customer rows only — controls join later in `buildPayload`, so this is not a plate-wide check.
 *  A repeated coordinate is a 422 at draft-create (409 at submit), so catch it before the call. */
export function countDuplicateWellPositions(samples: InstrumentRunFormValues['samples']): number {
  return countDuplicatesBy(samples, plateCoordinateKey);
}
